/**
 * Password Generator Service
 * Implements business logic for generating secure random passwords
 */

import { EncodingOperation, EncoderToolType } from '../types';
import { ProcessingError, ValidationError } from '../utils/errorHandler';

export interface PasswordOptions {
  length: number;
  includeUppercase: boolean;
  includeLowercase: boolean;
  includeNumbers: boolean;
  includeSymbols: boolean;
  quantity?: number;
}

const UPPERCASE = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const LOWERCASE = 'abcdefghijklmnopqrstuvwxyz';
const NUMBERS = '0123456789';
const SYMBOLS = '!@#$%^&*()_+-=[]{}|;:,.<>?';

export class PasswordGeneratorService {
  /**
   * Generate secure random password(s)
   */
  static async generate(options: PasswordOptions): Promise<EncodingOperation> {
    try {
      // Validate options
      this.validateOptions(options);

      // Generate passwords
      const quantity = options.quantity || 1;
      const passwords: string[] = [];

      for (let i = 0; i < quantity; i++) {
        passwords.push(this.generatePassword(options));
      }

      const output = quantity === 1 ? passwords[0] : passwords.join('\n');

      return {
        id: this.generateOperationId(),
        toolType: EncoderToolType.PASSWORD_GENERATOR,
        operationType: 'encode',
        inputData: JSON.stringify(options),
        outputData: output,
        timestamp: new Date(),
        status: 'success',
        metadata: {
          length: options.length,
          quantity,
          characterSets: {
            uppercase: options.includeUppercase,
            lowercase: options.includeLowercase,
            numbers: options.includeNumbers,
            symbols: options.includeSymbols
          },
          strength: this.calculateStrength(options)
        }
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }

      throw new ProcessingError(
        'Failed to generate password',
        EncoderToolType.PASSWORD_GENERATOR,
        'Please check your settings and try again'
      );
    }
  }

  /**
   * Validate password generation options
   */
  private static validateOptions(options: PasswordOptions): void {
    if (options.length < 8 || options.length > 128) {
      throw new ValidationError(
        'Password length must be between 8 and 128',
        EncoderToolType.PASSWORD_GENERATOR,
        'Enter a length between 8 and 128'
      );
    }

    if (!options.includeUppercase && !options.includeLowercase &&
        !options.includeNumbers && !options.includeSymbols) {
      throw new ValidationError(
        'At least one character set must be selected',
        EncoderToolType.PASSWORD_GENERATOR,
        'Select at least one character type'
      );
    }

    if (options.quantity && (options.quantity < 1 || options.quantity > 20)) {
      throw new ValidationError(
        'Quantity must be between 1 and 20',
        EncoderToolType.PASSWORD_GENERATOR,
        'Enter a quantity between 1 and 20'
      );
    }
  }

  /**
   * Generate a single password
   */
  private static generatePassword(options: PasswordOptions): string {
    let charset = '';
    const required: string[] = [];

    if (options.includeUppercase) {
      charset += UPPERCASE;
      required.push(UPPERCASE[this.getSecureRandomInt(UPPERCASE.length)]);
    }
    if (options.includeLowercase) {
      charset += LOWERCASE;
      required.push(LOWERCASE[this.getSecureRandomInt(LOWERCASE.length)]);
    }
    if (options.includeNumbers) {
      charset += NUMBERS;
      required.push(NUMBERS[this.getSecureRandomInt(NUMBERS.length)]);
    }
    if (options.includeSymbols) {
      charset += SYMBOLS;
      required.push(SYMBOLS[this.getSecureRandomInt(SYMBOLS.length)]);
    }

    // Generate remaining characters
    const remaining = options.length - required.length;
    const password: string[] = [...required];

    for (let i = 0; i < remaining; i++) {
      password.push(charset[this.getSecureRandomInt(charset.length)]);
    }

    // Shuffle password using Fisher-Yates algorithm
    for (let i = password.length - 1; i > 0; i--) {
      const j = this.getSecureRandomInt(i + 1);
      [password[i], password[j]] = [password[j], password[i]];
    }

    return password.join('');
  }

  /**
   * Get secure random integer using crypto.getRandomValues
   */
  private static getSecureRandomInt(max: number): number {
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    return array[0] % max;
  }

  /**
   * Calculate password strength
   */
  private static calculateStrength(options: PasswordOptions): string {
    let charsetSize = 0;
    if (options.includeUppercase) charsetSize += 26;
    if (options.includeLowercase) charsetSize += 26;
    if (options.includeNumbers) charsetSize += 10;
    if (options.includeSymbols) charsetSize += SYMBOLS.length;

    const entropy = options.length * Math.log2(charsetSize);

    if (entropy >= 80) return 'Very Strong';
    if (entropy >= 60) return 'Strong';
    if (entropy >= 40) return 'Moderate';
    if (entropy >= 25) return 'Weak';
    return 'Very Weak';
  }

  /**
   * Generate unique operation ID
   */
  private static generateOperationId(): string {
    return `password-generator-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Clears any cached data (for testing)
   */
  static clearCache(): void {
    // No-op: This service doesn't maintain cache
  }
}
